import { formatLabel } from '../utils/formatText';
import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Search, Filter, X, ZoomIn, ChevronLeft, ChevronRight, Info, Package, Shield, Share2, Check, Plus, Minus, ShoppingCart, Layers, Heart, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Banner, BannerGrid } from '../components/Banner';
import { ProductGridSkeleton } from '../components/ProductSkeleton';
import { ProductCard } from '../components/ProductCard';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useToast } from '../hooks/use-toast';
import { categories as defaultCategories, brands as mockBrands } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Ícone WhatsApp (usado no ProductModal)
const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={`${className} fill-current`}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Função para gerar slug do produto
const generateSlug = (product) => {
  if (!product) return '';
  let slug = product.name.toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[áàã]/g, 'a')
    .replace(/[éè]/g, 'e')
    .replace(/[íì]/g, 'i')
    .replace(/[óòõ]/g, 'o')
    .replace(/[úù]/g, 'u')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug}-${product.id?.slice(0, 8) || ''}`;
};

// Modal de Detalhes do Produto com seleção de variações
const ProductModal = ({ product, isOpen, onClose, brands, categories }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({}); // { variationId: quantity }
  const [simpleQuantity, setSimpleQuantity] = useState(1);
  
  const { settings } = useSiteSettings();
  const { addProduct, addVariation, isInCart, getCartItem } = useQuoteCart();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Reset ao abrir novo produto
  useEffect(() => {
    if (isOpen && product) {
      setSelectedVariations({});
      setSimpleQuantity(1);
      setCurrentImageIndex(0);
    }
  }, [isOpen, product?.id]);

  if (!isOpen || !product) return null;

  const images = product.images?.length > 0 ? product.images : ['https://placehold.co/600x600?text=Produto'];
  const hasVariations = product.variations && product.variations.length > 0;
  
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  // Encontrar logo da marca
  const brandLogo = brands.find(b => b.name?.toLowerCase() === product.brand?.toLowerCase())?.logo;
  const categoryName = categories.find(c => c.id === product.category)?.name || product.category;

  // Compartilhar
  const handleShare = async () => {
    const productUrl = `${window.location.origin}/produto/${generateSlug(product)}`;
    try {
      await navigator.clipboard.writeText(productUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.log('Erro ao copiar');
    }
  };

  // WhatsApp
  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name}`);
    window.open(`https://wa.me/${settings.site_whatsapp}?text=${message}`, '_blank');
  };

  // Variação selecionada/quantidade
  const toggleVariation = (variationId) => {
    setSelectedVariations(prev => {
      if (prev[variationId]) {
        const { [variationId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [variationId]: 1 };
    });
  };

  const updateVariationQuantity = (variationId, delta) => {
    setSelectedVariations(prev => {
      const current = prev[variationId] || 0;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [variationId]: newQty };
    });
  };

  // Adicionar ao orçamento
  const handleAddToQuote = () => {
    if (hasVariations) {
      const selectedIds = Object.keys(selectedVariations);
      if (selectedIds.length === 0) {
        toast({ title: "Selecione pelo menos uma opção", variant: "destructive" });
        return;
      }
      
      selectedIds.forEach(varId => {
        const variation = product.variations.find(v => v.id === varId);
        const qty = selectedVariations[varId];
        if (variation && qty > 0) {
          addVariation(product, variation, qty);
        }
      });
      
      toast({ 
        title: `${selectedIds.length} ${selectedIds.length === 1 ? 'item adicionado' : 'itens adicionados'} ao orçamento!`,
        description: "Vá para o orçamento para finalizar"
      });
    } else {
      addProduct(product, simpleQuantity);
      toast({ 
        title: `${product.name} adicionado ao orçamento!`,
        description: `Quantidade: ${simpleQuantity}`
      });
    }
  };

  // Ir para orçamento
  const goToQuote = () => {
    handleAddToQuote();
    onClose();
    navigate('/orcamento');
  };

  // Total selecionado
  const totalSelectedQty = hasVariations 
    ? Object.values(selectedVariations).reduce((a, b) => a + b, 0)
    : simpleQuantity;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 flex justify-between items-center p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {brandLogo && <img src={brandLogo} alt={product.brand} className="h-6 object-contain" />}
            <Badge className="bg-[#0A0A0A] text-white rounded-full">{product.brand}</Badge>
            {product.is_promo && <Badge className="bg-[#EA1266] text-white">Promoção</Badge>}
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
          {/* Imagens */}
          <div className="space-y-4">
            <div className="relative aspect-square rounded-2xl overflow-hidden bg-gray-50 cursor-zoom-in" onClick={() => setIsZoomed(!isZoomed)}>
              <img src={images[currentImageIndex]} alt={product.name} className={`w-full h-full object-contain transition-transform duration-300 ${isZoomed ? 'scale-150' : ''}`} />
              {images.length > 1 && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white">
                    <ChevronLeft size={20} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center hover:bg-white">
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
              <button className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
                <ZoomIn size={18} />
              </button>
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto py-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setCurrentImageIndex(i)} className={`w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all ${i === currentImageIndex ? 'border-[#EA1266]' : 'border-transparent'}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Detalhes */}
          <div className="space-y-5">
            <div>
              <p className="text-sm text-gray-500 font-medium mb-1">{formatLabel(categoryName)}</p>
              <h2 className="text-2xl font-bold text-[#0A0A0A]">{product.name}</h2>
              {product.size && <p className="text-sm text-gray-500 mt-1">Tamanho: {product.size}</p>}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Descrição</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{product.description || 'Entre em contato para mais informações.'}</p>
            </div>

            {/* Variações com seleção e quantidade */}
            {hasVariations && (
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers size={18} className="text-[#EA1266]" />
                  <h3 className="font-semibold text-gray-900">Selecione as Opções</h3>
                </div>
                <p className="text-xs text-gray-500 mb-3">Clique para selecionar e ajuste a quantidade de cada item:</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {product.variations.map((variation) => {
                    const isSelected = selectedVariations[variation.id] !== undefined;
                    const qty = selectedVariations[variation.id] || 0;
                    const alreadyInCart = isInCart(product.id, variation.id);
                    const cartItem = getCartItem(product.id, variation.id);
                    
                    return (
                      <div 
                        key={variation.id}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                          isSelected ? 'border-[#EA1266] bg-pink-50' : 'border-gray-100 bg-white hover:border-gray-200'
                        }`}
                      >
                        {variation.image && (
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex-shrink-0">
                            <img src={variation.image} alt={variation.name} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{variation.name}</p>
                          {variation.size && <p className="text-xs text-gray-500">{variation.size}</p>}
                          {alreadyInCart && (
                            <p className="text-xs text-[#64BC46]">✓ No orçamento ({cartItem?.quantity})</p>
                          )}
                        </div>
                        
                        {isSelected ? (
                          <div className="flex items-center gap-1">
                            <button 
                              onClick={() => updateVariationQuantity(variation.id, -1)}
                              className="w-7 h-7 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50"
                            >
                              <Minus size={14} />
                            </button>
                            <span className="w-8 text-center font-semibold text-sm">{qty}</span>
                            <button 
                              onClick={() => updateVariationQuantity(variation.id, 1)}
                              className="w-7 h-7 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50"
                            >
                              <Plus size={14} />
                            </button>
                            <button 
                              onClick={() => toggleVariation(variation.id)}
                              className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 ml-1"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button 
                            onClick={() => toggleVariation(variation.id)}
                            className="px-3 py-1.5 rounded-full bg-[#EA1266] text-white text-xs font-medium hover:bg-[#d00f58]"
                          >
                            Selecionar
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {Object.keys(selectedVariations).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {Object.keys(selectedVariations).length} {Object.keys(selectedVariations).length === 1 ? 'opção' : 'opções'} • {totalSelectedQty} {totalSelectedQty === 1 ? 'unidade' : 'unidades'}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Produto simples - quantidade */}
            {!hasVariations && (
              <div className="bg-gray-50 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">Quantidade:</span>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setSimpleQuantity(Math.max(1, simpleQuantity - 1))}
                      className="w-9 h-9 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="w-10 text-center font-bold text-lg">{simpleQuantity}</span>
                    <button 
                      onClick={() => setSimpleQuantity(simpleQuantity + 1)}
                      className="w-9 h-9 rounded-full bg-white border flex items-center justify-center hover:bg-gray-50"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
                {isInCart(product.id) && (
                  <p className="text-xs text-[#64BC46] mt-2">✓ Já está no orçamento ({getCartItem(product.id)?.quantity} unidades)</p>
                )}
              </div>
            )}

            {/* Garantias */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl">
                <Shield size={18} className="text-[#64BC46]" />
                <span className="text-xs font-medium text-gray-700">100% Original</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-pink-50 rounded-xl">
                <Package size={18} className="text-[#EA1266]" />
                <span className="text-xs font-medium text-gray-700">Entrega Rápida</span>
              </div>
            </div>

            {/* Botões de ação */}
            <div className="space-y-3 pt-2">
              <Button onClick={handleWhatsApp} className="w-full bg-[#64BC46] hover:bg-[#56a33c] text-white py-5 rounded-full">
                <WhatsAppIcon className="w-5 h-5 mr-2" />
                Consultar Preço
              </Button>
              
              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={handleAddToQuote}
                  disabled={hasVariations && Object.keys(selectedVariations).length === 0}
                  className={`w-full py-4 rounded-full ${
                    hasVariations && Object.keys(selectedVariations).length === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#EA1266] hover:bg-[#d00f58] text-white'
                  }`}
                >
                  <Plus size={18} className="mr-1" />
                  Adicionar ({totalSelectedQty})
                </Button>
                <Button 
                  onClick={goToQuote}
                  disabled={hasVariations && Object.keys(selectedVariations).length === 0}
                  variant="outline"
                  className="w-full py-4 rounded-full border-[#EA1266] text-[#EA1266] hover:bg-[#EA1266] hover:text-white"
                >
                  <ShoppingCart size={18} className="mr-1" />
                  Ir ao Orçamento
                </Button>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={handleShare} className="flex-1 py-3 rounded-full">
                  {copied ? <Check size={16} className="mr-1 text-green-600" /> : <Share2 size={16} className="mr-1" />}
                  {copied ? 'Copiado!' : 'Compartilhar'}
                </Button>
                <Link to={`/produto/${generateSlug(product)}`} className="flex-1">
                  <Button variant="outline" className="w-full py-3 rounded-full">
                    <Info size={16} className="mr-1" />
                    Ver Detalhes
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente Principal do Catálogo
export const Catalogo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings } = useSiteSettings();
  const { totalItems, isInCart } = useQuoteCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState(mockBrands);
  const [categories, setCategories] = useState(defaultCategories);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categoria') || searchParams.get('cat') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('marca') || searchParams.get('brand') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [banners, setBanners] = useState([]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, brandsRes, categoriesRes, bannersRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/products`),
          axios.get(`${BACKEND_URL}/api/brands`),
          axios.get(`${BACKEND_URL}/api/categories`),
          axios.get(`${BACKEND_URL}/api/banners`).catch(() => ({ data: [] }))
        ]);
        
        if (productsRes.data?.length > 0) setProducts(productsRes.data);
        if (brandsRes.data?.length > 0) setBrands(brandsRes.data);
        if (categoriesRes.data?.length > 0) setCategories(categoriesRes.data);
        if (bannersRes.data?.length > 0) setBanners(bannersRes.data.filter(b => b.is_active));
        
        // Track page view
        await axios.post(`${BACKEND_URL}/api/track/page?page=catalogo`).catch(() => {});
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtrar produtos
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = !searchTerm || 
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = !selectedCategory || product.category?.toLowerCase() === selectedCategory.toLowerCase();
      const matchesBrand = !selectedBrand || product.brand?.toLowerCase() === selectedBrand.toLowerCase();
      return matchesSearch && matchesCategory && matchesBrand;
    });
  }, [products, searchTerm, selectedCategory, selectedBrand]);

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedCategory) params.set('categoria', selectedCategory);
    if (selectedBrand) params.set('marca', selectedBrand);
    setSearchParams(params, { replace: true });
  }, [searchTerm, selectedCategory, selectedBrand, setSearchParams]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedBrand('');
  };

  const hasActiveFilters = searchTerm || selectedCategory || selectedBrand;

  // Banners do catálogo
  const catalogBanners = banners.filter(b => b.position === 'catalog_featured' || b.position === 'catalog_side');
  const featuredBanner = catalogBanners.find(b => b.position === 'catalog_featured');
  const sideBanners = catalogBanners.filter(b => b.position === 'catalog_side');

  // WhatsApp para produto
  const handleWhatsApp = (product) => {
    const message = encodeURIComponent(`Olá! Tenho interesse no produto: ${product.name}`);
    window.open(`https://wa.me/${settings.site_whatsapp}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="catalogo-page">
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white py-16 md:py-20">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#EA1266] mb-3 block">Nossos Produtos</span>
              <h1 className="text-4xl sm:text-5xl font-medium tracking-tight" data-testid="catalogo-title">Catálogo</h1>
              <p className="text-base text-gray-400 mt-3 font-medium max-w-lg">
                Encontre os melhores produtos de beleza para você
              </p>
            </div>
            
            {/* Botão do orçamento */}
            {totalItems > 0 && (
              <Link to="/orcamento">
                <Button className="bg-[#EA1266] hover:bg-[#d00f58] text-white rounded-full px-6 py-5 shadow-lg">
                  <ShoppingCart size={18} className="mr-2" />
                  Ver Orçamento ({totalItems})
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Filtros */}
      <section className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
            {/* Busca */}
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-11 pr-4 py-5 rounded-full border-gray-200 focus:border-[#EA1266] focus:ring-[#EA1266]/20"
                data-testid="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Categorias */}
            <div className="flex gap-2 overflow-x-auto pb-2 md:pb-0 hide-scrollbar">
              <button
                onClick={() => setSelectedCategory('')}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  !selectedCategory ? 'bg-[#EA1266] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Todos
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? '' : cat.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === cat.id ? 'bg-[#EA1266] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Filtro de marca (mobile toggle) */}
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="md:hidden rounded-full">
              <Filter size={16} className="mr-2" />
              Filtros
            </Button>
          </div>

          {/* Filtros expandidos (mobile) */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 md:hidden">
              <div className="flex flex-wrap gap-2">
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white"
                >
                  <option value="">Todas as marcas</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.name}>{formatLabel(brand.name)}</option>
                  ))}
                </select>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium text-red-500 bg-red-50 hover:bg-red-100">
                    Limpar filtros
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Filtro de marca (desktop) */}
          <div className="hidden md:flex gap-2 mt-4 items-center">
            <span className="text-sm text-gray-500 font-medium">Marca:</span>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="px-4 py-2 rounded-full border border-gray-200 text-sm bg-white"
            >
              <option value="">Todas as marcas</option>
              {brands.map(brand => (
                <option key={brand.id} value={brand.name}>{formatLabel(brand.name)}</option>
              ))}
            </select>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="px-4 py-2 rounded-full text-sm font-medium text-red-500 bg-red-50 hover:bg-red-100 ml-auto">
                <X size={14} className="inline mr-1" />
                Limpar filtros
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Banner destaque */}
      {featuredBanner && (
        <section className="container mx-auto px-6 md:px-12 py-6">
          <Banner banner={featuredBanner} />
        </section>
      )}

      {/* Produtos */}
      <section className="container mx-auto px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500 font-medium">
            {filteredProducts.length} {filteredProducts.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
          </p>
        </div>

        <div className="flex gap-8">
          {/* Grid de produtos */}
          <div className="flex-1">
            {loading ? (
              <ProductGridSkeleton count={8} />
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <Package size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-medium text-gray-900 mb-2">Nenhum produto encontrado</h3>
                <p className="text-gray-500 mb-6">Tente ajustar os filtros ou buscar por outro termo</p>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outline" className="rounded-full">
                    Limpar filtros
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredProducts.map((product) => {
                  const hasVariations = product.variations && product.variations.length > 0;
                  const inCart = !hasVariations && isInCart(product.id);
                  
                  return (
                    <ProductCard
                      key={product.id}
                      product={product}
                      inCart={inCart}
                      onCardClick={() => setSelectedProduct(product)}
                      onWhatsAppClick={handleWhatsApp}
                      onToggleWishlist={toggleWishlist}
                      isInWishlist={isInWishlist(product.id)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Banners laterais (desktop) */}
          {sideBanners.length > 0 && (
            <div className="hidden lg:block w-[280px] space-y-4 flex-shrink-0">
              <BannerGrid banners={sideBanners} />
            </div>
          )}
        </div>
      </section>

      {/* Modal do produto */}
      <ProductModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        brands={brands}
        categories={categories}
      />
    </div>
  );
};
